import React, { useMemo, useState, useEffect } from 'react';
import {
  Text,
  Paper,
  Box,
  Loader,
  Alert,
  Card,
  Anchor,
  Center,
  Group,
  Stack,
  Switch,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../SQLEditorPopup';

// Simplified Legend component with more concise descriptions
const ColorLegend = () => (
  <Card shadow="sm" padding="xs" radius="sm" withBorder>
    <Text size="sm" fw={500} mb={4}>
      Inventory Health Status
    </Text>
    <Stack gap={4}>
      {[
        { color: '#2A7040', label: 'Green: 15% or more ahead of goal' },
        { color: '#A07D32', label: 'Yellow: 1-14% ahead of goal' },
        { color: '#944848', label: 'Red: Behind goal' },
      ].map(({ color, label }) => (
        <Group key={label} gap={6} align="center">
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: color,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Group>
      ))}
    </Stack>
  </Card>
);

// This function determines the color status for inventory health categories
// Updated to match the simplified legend logic
const getInventoryHealthStatus = (value, ageBucket) => {
  // Goals for each age bucket
  const goals = {
    '0-90': 56,   // At least 56%
    '91-180': 26, // At most 26%
    '181-365': 16, // At most 16%
    '366+': 2     // At most 2%
  };

  const goal = goals[ageBucket];

  // For 0-90 days, higher percentage is better (want minimum 56%)
  if (ageBucket === '0-90') {
    // 15% or more ahead of goal (≥ 64.4%)
    if (value >= goal * 1.15) return 'green';
    // 1-14% ahead of goal (56-64.3%)
    if (value >= goal) return 'yellow';
    // Behind goal (<56%)
    return 'red';
  }
  // For all other buckets, lower percentage is better (want maximum %)
  else {
    // 15% or more ahead of goal (≤ 85% of maximum)
    if (value <= goal * 0.85) return 'green';
    // 1-14% ahead of goal (85-99% of maximum)
    if (value <= goal) return 'yellow';
    // Behind goal (>100% of maximum)
    return 'red';
  }
};

function PurchasingIndividualInventoryHealthDetails() {
  const { ccode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const filterParam = searchParams.get('filter')?.toUpperCase();

  // Initialize state based on URL parameter, defaulting to true if no filter is specified
  const [onlyPurchasingWarehouses, setOnlyPurchasingWarehouses] = useState(() => {
    if (filterParam) {
      return filterParam === 'IN';
    }
    return true; // Default to true when no filter parameter is present
  });

  // State for technical pop-ups
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);

  // Update state when URL changes
  useEffect(() => {
    const newFilterParam = searchParams.get('filter')?.toUpperCase();
    if (newFilterParam) {
      setOnlyPurchasingWarehouses(newFilterParam === 'IN');
    }
  }, [location.search]);

  // Generate the filter value based on checkbox state
  const warehouseFilter = onlyPurchasingWarehouses ? 'IN' : 'OUT';

  // Build URL with ccode and filter parameters
  const url = `${config.apiBaseUrl}/supply-chain/purchasing/individual/scorecard/inventory-health-details?ccode=${ccode}&filter=${warehouseFilter}`;
  const { data, isLoading, error } = useAuthenticatedData(url);
  const { data: purchaserName, isLoading: isLoadingName, error: errorName } = useAuthenticatedData(`${config.apiBaseUrl}/data/name?ccode=${ccode}`);

  const handleFilterChange = (event) => {
    const newValue = event.currentTarget.checked;
    setOnlyPurchasingWarehouses(newValue);
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('filter', newValue ? 'IN' : 'OUT');
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  // Process the data into rows for the table.
  // Transform the data structure to create rows for each age category
  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    const transformedRows = [];
    let rowId = 0;
    
    data.forEach((item) => {
      const values = item.values;
      
      // Create rows for each age category that has data
      const ageCategories = [
        { key: '90', label: '0-90 days', spend: values.spend90, percent: values.percent90 },
        { key: '180', label: '91-180 days', spend: values.spend180, percent: values.percent180 },
        { key: '365', label: '181-365 days', spend: values.spend365, percent: values.percent365 },
        { key: '365plus', label: '366+ days', spend: values.spend365plus, percent: values.percent365plus },
      ];
      
      ageCategories.forEach((category) => {
        if (category.spend > 0 || category.percent > 0) {
          transformedRows.push({
            id: rowId++,
            ccode: values.ccode,
            agecategory: category.label,
            totalcostdollars: category.spend,
            percentoftotalcost: category.percent,
            // Calculate estimated sale value and GM (placeholder calculations)
            totalsalevaluedollars: category.spend * 1.3, // Assuming 30% markup
            expectedgmdollars: category.spend * 0.3, // Assuming 30% margin
            expectedgmpercent: 30, // Placeholder
          });
        }
      });
    });
    
    return transformedRows;
  }, [data]);

  // Generate export filename.
  const generateExportFileName = () => `Purchasing-${purchaserName}-inventory-health-details`;

  // Define columns for the table.
  const columns = useMemo(
    () => [
      { accessorKey: 'ccode', header: 'Buyer Code', size: 100 },
      { accessorKey: 'agecategory', header: 'Age Category', size: 120 },
      {
        accessorKey: 'totalcostdollars',
        header: 'Total Cost ($)',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'percentoftotalcost',
        header: '% of Total Cost',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },
      {
        accessorKey: 'totalsalevaluedollars',
        header: 'Total Sale Value ($)',
        size: 170,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'expectedgmdollars',
        header: 'Expected GM ($)',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'expectedgmpercent',
        header: 'Expected GM (%)',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },
    ],
    []
  );

  const getRowStyle = (row) => {
    const ageCategory = row.original.agecategory;
    const percentOfTotalCost = row.original.percentoftotalcost;

    // Determine which category the row falls into
    let category = '';
    if (ageCategory?.includes('0-90')) {
      category = '0-90';
    } else if (ageCategory?.includes('91-180')) {
      category = '91-180';
    } else if (ageCategory?.includes('181-365')) {
      category = '181-365';
    } else if (ageCategory?.includes('366+')) {
      category = '366+';
    }

    // Apply the color logic
    const status = getInventoryHealthStatus(percentOfTotalCost, category);
    
    if (status === 'green') {
      return {
        backgroundColor: '#2A7040',
        color: 'white',
      };
    }
    if (status === 'yellow') {
      return {
        backgroundColor: '#A07D32',
        color: 'white',
      };
    }
    if (status === 'red') {
      return {
        backgroundColor: '#944848',
        color: 'white',
      };
    }
    return {};
  };

  const table = useMantineReactTable({
    columns,
    data: rows,
    enableGlobalFilter: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'xs',
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      style: getRowStyle(row),
    }),
    mantineTableProps: {
      style: { fontSize: '0.875rem' },
    },
    mantineBottomToolbarProps: {
      style: { padding: '8px' },
    },
  });

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      {/* Header with title and checkbox */}
      <Group justify="space-between" align="center" mb="md">
        <Text
          component="h1"
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
            marginBottom: 0,
            textAlign: 'left'
          }}
        >
          Purchasing Individual Scorecard - Inventory Health Details - {isLoadingName ? 'Loading...' : purchaserName}
        </Text>
        
        <Switch
          label="Only purchasing warehouses"
          checked={onlyPurchasingWarehouses}
          onChange={handleFilterChange}
          size="sm"
        />
      </Group>

      {/* Technical options: small links for debugging */}
      <Group mb="sm" gap="sm">
 
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=purchasing_individual_inventory_health_summary',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Summary Query
        </Anchor>
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=purchasing_individual_inventory_health_details',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Details Query
        </Anchor>
      </Group>

      {/* About the Data and Legend Block */}
      <Group mb="sm" align="stretch" grow>
        {/* About this Data card */}
        <Card shadow="sm" padding="xs" radius="sm" withBorder>
          <Text size="sm" fw={500} mb={4}>
            About the Data
          </Text>
          <Text size="xs" c="dimmed">
            This shows the age of material purchased by {isLoadingName ? 'this purchaser' : purchaserName}, the cost to purchase it, and the potential value for its sale if it were to sell
            at current Pentagon sell prices. This represents the inventory that was purchased on POs for stock by {isLoadingName ? 'this purchaser' : purchaserName} currently on the shelf.
          </Text>
        </Card>
        {/* Legend */}
        <ColorLegend />
      </Group>

      <Paper shadow="sm" p="sm" style={{ height: 400 }}>
        <Box style={{ height: '100%' }}>
          {isLoading || isLoadingName ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : errorName ? (
            <Alert color="red" title="Error">
              {errorName.message}
            </Alert>
          ) : (
            <MantineReactTable table={table} />
          )}
        </Box>
      </Paper>

  
    </Paper>
  );
}

export default PurchasingIndividualInventoryHealthDetails;